import React, { useRef, useState, useEffect } from "react";
import { getAccessToken } from "../../storage/storage";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { GoIssueClosed } from "react-icons/go";
import { IoMdArrowBack, IoMdCloseCircleOutline } from "react-icons/io";
import { CiSaveDown1 } from "react-icons/ci";
import { useNavigate, useParams } from "react-router-dom";
import { FormatDate } from "../../utils/FormateDate";
import { getApiErrorMessage } from "../../utils/apiError";
import CommentModal from "./components/CommentModal";
function InvoiceDetail(props) {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { setShow, invoiceData: propInvoiceData, setInvoiceData: propSetInvoiceData } = props;
  const cancelButtonRef = useRef(null);
  const token = getAccessToken();
  const pdfRef = useRef();
  const Navigate = useNavigate();
  const { invoiceId } = useParams(); // Get invoice ID from URL params
  const [isOpenModal, setIsOpenModal] = useState(false);
  // State for API-fetched data
  const [apiInvoiceData, setApiInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine which data to use (props or API)
  const invoiceData = propInvoiceData || apiInvoiceData;
  const setInvoiceData = propSetInvoiceData || setApiInvoiceData;

  // Fetch invoice data when component mounts if no props data is provided
  useEffect(() => {
    // If we don't have invoice data from props, fetch it from API
    if (!propInvoiceData && invoiceId) {
      const fetchInvoiceData = async () => {
        try {
          setLoading(true);
          const response = await axios.get(
            `${BASE_URL}/api/stays/invoice/?invoice_id=${invoiceId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          // Assuming the API returns an array with a single invoice object
          if (response.data && response.data.length > 0) {
            setApiInvoiceData(response.data[0]);
          } else {
            setError("Invoice not found");
          }
        } catch (err) {
          setError("Failed to load invoice data");
          console.error("Error fetching invoice:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchInvoiceData();
    } else if (propInvoiceData) {
      // If we have data from props, no need to load
      setLoading(false);
    }
  }, [propInvoiceData, invoiceId, BASE_URL, token]);

  const handleDownloadText = () => {
    if (!invoiceData) return;

    const doc = new jsPDF();

    // Invoice Header
    doc.setFontSize(16);
    doc.text("INVOICE", 105, 10, null, null, "center");
    doc.setFontSize(12);
    doc.text(`Invoice #: ${invoiceData?.invoice_number}`, 10, 20);
    doc.text(
      `Date: ${new Date(invoiceData?.created_at).toLocaleDateString()}`,
      10,
      30
    );

    // Business Details
    doc.text("Business:", 10, 40);
    doc.text(`${invoiceData?.business_details?.company_name}`, 10, 45);
    doc.text(`${invoiceData?.business_details?.address_line1}`, 10, 50);
    doc.text(
      `VAT Number: ${invoiceData?.business_details?.vat_number}`,
      10,
      55
    );

    // Bill To Section
    doc.text("Bill To:", 10, 65);
    doc.text(`${invoiceData?.property?.name}`, 10, 70);
    doc.text(`${invoiceData?.property?.address_line1}`, 10, 75);
    doc.text(
      `${invoiceData?.property?.city}, ${invoiceData?.property?.country}`,
      10,
      80
    );

    // Invoice Items Table
    autoTable(doc, {
      startY: 90,
      head: [["Task", "Qty", "Rate (£)", "Total (£)"]],
      body: invoiceData?.tasks.map((task) => [
        task.task_name,
        task.quantity,
        task.rate,
        task.total,
      ]),
    });

    let finalY = doc.lastAutoTable.finalY + 10;

    // Subtotal & Total
    doc.text(`Subtotal: £${invoiceData?.subtotal}`, 10, finalY);
    doc.text(`Total: £${invoiceData?.total_amount}`, 10, finalY + 10);

    // Payment Details
    doc.text("Payment Details:", 10, finalY + 20);
    doc.text(
      `Account Name: ${invoiceData?.bank_details?.account_name}`,
      10,
      finalY + 25
    );
    doc.text(
      `Account Number: ${invoiceData?.bank_details?.account_number}`,
      10,
      finalY + 30
    );
    doc.text(
      `Sort Code: ${invoiceData?.bank_details?.sort_code}`,
      10,
      finalY + 35
    );
    doc.text(
      `Payment Terms: Pay within ${invoiceData?.bank_details?.payment_terms} days.`,
      10,
      finalY + 40
    );

    // Cleaner Contact
    doc.text("Cleaner Contact:", 10, finalY + 50);
    doc.text(`Email: ${invoiceData?.cleaner_email}`, 10, finalY + 55);
    doc.text(`Phone: ${invoiceData?.cleaner_phone || "N/A"}`, 10, finalY + 60);

    // Save PDF
    doc.save("invoice.pdf");
  };

  const handleApprove = async (id) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/api/invoices/${id}/approve/`,
        {}, // Empty body if required
        {
          headers: {
            Authorization: `Bearer ${token}`, // Token should be in headers, not body
            "Content-Type": "application/json",
          },
        }
      );

      // Immediately update invoiceData to reflect the new status
      setInvoiceData((prevState) => ({
        ...prevState,
        status: "approved",
      }));

      Swal.fire({
        icon: "success",
        title: response?.data?.message || "Invoice Approved",
        timer: 2000,
        showConfirmButton: false,
        position: "center",
      });
      // setShow(false);
      Navigate("/other-tasks");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: getApiErrorMessage(error, "Something went wrong!"),
        timer: 2000,
        showConfirmButton: false,
        position: "center",
      });
    }
  };
  const handleOpenModal = () => {
    setIsOpenModal(true);
  };
  const handleReject = async (id) => {
    try {
      const response = await axios.put(
        `${BASE_URL}/api/invoices/${id}/reject/`,
        {}, // Empty body if required
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Immediately update invoiceData to reflect the new status
      setInvoiceData((prevState) => ({
        ...prevState,
        status: "paid",
      }));

      Swal.fire({
        icon: "success",
        title: "Invoice Rejected",
        timer: 2000,
        showConfirmButton: false,
        position: "center",
      });
      // setShow(false);
      Navigate("/other-tasks");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: getApiErrorMessage(error, "Something went wrong!"),
        timer: 2000,
        showConfirmButton: false,
        position: "center",
      });
    }
  };

  const handleMarkAsPaid = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.put(
        `${BASE_URL}/api/invoices/${invoiceData?.id}/mark-paid/`,
        {}, // If you need to send any request body, add it here
        {
          headers: {
            Authorization: `Bearer ${token}`, // Send the token in the Authorization header
          },
        }
      );

      // Success Swal
      if (response.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Invoice Marked as Paid!",
          text: "The invoice has been successfully marked as paid.",
          timer: 2000,
          showConfirmButton: false,
        });
        setInvoiceData((prevState) => ({
          ...prevState,
          status: "paid",
        }));
        Navigate("/other-tasks");
      }
    } catch (error) {
      // Error Swal
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "There was an error marking the invoice as paid.",
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error marking invoice as paid", error);
      setError("There was an error marking the invoice as paid.");
    } finally {
      setLoading(false);
    }
  };

  const subtotal = parseFloat(invoiceData?.subtotal) || 0;
  const vatRate = parseFloat(invoiceData?.business_details?.vat_rate) || 0;
  const vatAmount = (subtotal * vatRate) / 100;
  const total = parseFloat((subtotal + vatAmount).toFixed(2));

  const formattedTotal = total.toFixed(2);

  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl">Loading invoice...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <>
      <div className="">
        <button
          onClick={() => setShow ? setShow(false) : Navigate("/other-tasks")}
          className="flex items-center gap-2 text-xl font-bold text-[#586989] hover:text-blue-600 p-8"
        >
          <IoMdArrowBack />
          <p>Back</p>
        </button>
        <div className="flex flex-col justify-center items-start w-full">
          <div className="max-w-2xl mx-auto bg-white md:p-8 p-2 shadow-xl rounded-md border mb-8 relative">
            {invoiceData && invoiceData.tasks?.length > 0 ? (
              <>
                <h2 className="md:text-3xl text-lg text-center font-bold text-[#586989] pb-1">
                  Invoice {invoiceData?.invoice_number}
                </h2>

                <p className="text-[#586989] text-center flex items-center gap-2 justify-center">
                  <span>Submitted on </span>
                  {invoiceData?.created_at && (
                    <span>
                      {new Date(invoiceData?.created_at)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, "-")}
                    </span>
                  )}
                  <span>by {invoiceData?.cleaner_name}</span>
                </p>
                {invoiceData?.status !== "pending" && (
                  <>
                    {invoiceData?.status !== "rejected" && (
                      <p className="flex items-center justify-center gap-3 text-[#586989]">
                        Approved on
                        <p className="flex gap-1">
                          {invoiceData?.action_date
                            ? FormatDate(invoiceData.action_date)
                            : "-"}
                          <p>by</p>
                          <p>{invoiceData?.owner?.first_name}</p>
                          <p>{invoiceData?.owner?.last_name}</p>
                        </p>
                      </p>
                    )}
                    {invoiceData?.status === "rejected" && (
                      <p className="flex items-center justify-center gap-3 text-[#586989]">
                        Rejected on
                        <p className="flex gap-1">
                          {invoiceData?.action_date
                            ? FormatDate(invoiceData.action_date)
                            : "-"}
                          <p>by</p>
                          <p>{invoiceData?.owner?.first_name}</p>
                          <p>{invoiceData?.owner?.last_name}</p>
                        </p>
                      </p>
                    )}
                  </>
                )}
                {invoiceData?.status === "paid" && (
                  <p className="flex items-center justify-center gap-3 text-[#586989]">
                    Paid on
                    <p className="flex gap-1">
                      <p>
                        {" "}
                        {invoiceData?.paid_date
                          ? FormatDate(invoiceData.paid_date)
                          : "-"}
                      </p>
                      by
                      <p>{invoiceData?.owner?.first_name}</p>
                      <p>{invoiceData?.owner?.last_name}</p>
                    </p>
                  </p>
                )}

                {invoiceData?.status === "paid" && (
                  <div className="mt-2 font-semibold md:text-[16px] text-[12px] flex items-center justify-center gap-3 border border-[#106A3A] text-[#586989] px-4 py-3 rounded w-full">
                    <IoMdCloseCircleOutline className="font-semibold text-[#586989]" />
                    This task sheet has been Paid.
                  </div>
                )}
                {invoiceData?.status === "approved" && (
                  <button
                    onClick={handleMarkAsPaid}
                    className="md:text-[16px] text-[12px] mt-2 flex items-center justify-center gap-3 hover:border-none border border-[#106A3A] hover:bg-[#586989] hover:text-white text-[#106A3A] px-4 py-3 rounded w-full"
                  >
                    <GoIssueClosed />
                    Mark as Paid
                  </button>
                )}
                {invoiceData?.status === "rejected" && (
                  <div className="mt-2 font-semibold md:text-[16px] text-[12px] flex items-center justify-center gap-3 border border-[#106A3A] text-[#586989] px-4 py-3 rounded w-full">
                    <IoMdCloseCircleOutline className="font-semibold text-[#586989]" />
                    This task sheet has been Rejected.
                  </div>
                )}
                {invoiceData?.rejection_reason && (
                  <div className="mt-2 font-semibold md:text-[16px] text-[12px] flex items-center justify-center gap-3 border border-[#106A3A] text-[#586989] px-4 py-3 rounded w-full ">
                    <p className="font-semibold text-[#586989]">Rejection Reason:</p>
                    <p className="text-gray-500">{invoiceData.rejection_reason}</p>
                  </div>
                )}
                {!(
                  invoiceData?.status === "approved" ||
                  invoiceData?.status === "rejected"
                ) &&
                  invoiceData?.status !== "paid" && (
                    <div className="flex w-full gap-4 mt-4">
                      <button
                        onClick={() => handleApprove(invoiceData?.id)}
                        className="md:text-[16px] text-[12px] flex items-center justify-center gap-3 hover:border-none border border-[#106A3A] hover:bg-[#586989] hover:text-white text-[#106A3A] px-4 py-3 rounded w-full"
                      >
                        <GoIssueClosed />
                        Approve Task Sheet
                      </button>

                      <button
                        onClick={() => handleOpenModal()}
                        // onClick={() => handleReject(invoiceData?.id)}
                        className="md:text-[16px] text-[12px] flex items-center justify-center gap-3 hover:border-none border border-[#106A3A] hover:bg-[#586989] hover:text-white text-[#106A3A] px-4 py-3 rounded w-full"
                      >
                        <IoMdCloseCircleOutline />
                        Reject Task Sheet
                      </button>
                    </div>

                  )}

                {/* Invoice Details */}
                <div
                  ref={pdfRef}
                  className="border border-[#106A3A] mt-6 p-6 rounded-[8px] flex flex-col gap-10"
                >
                  <div className="w-full h-full rounded-full flex items-center justify-center">
                    <img
                      src={invoiceData?.business_details?.company_image}
                      alt=""
                      className="w-10 h-10 object-center"
                    />
                  </div>
                  {/* Invoice Header */}
                  <div className="w-full flex justify-between items-center pb-4">
                    <div>
                      <h3 className="text-lg font-semibold">INVOICE</h3>
                      <p className="text-gray-700">
                        Invoice #: {invoiceData?.invoice_number}
                      </p>
                      <p className="text-gray-700">
                        Date:{" "}
                        {new Date(invoiceData?.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="w-[45%]">
                      <h4 className="font-semibold">
                        {invoiceData?.business_details?.company_name}
                      </h4>
                      <div className="flex flex-wrap">
                        <p>{invoiceData?.business_details?.address_line1}</p>
                        <p className="flex flex-wrap gap-1">
                          <p>{invoiceData?.business_details?.address_line2}</p>
                          <p>{invoiceData?.business_details?.city}</p>
                          <p>{invoiceData?.business_details?.county}</p>
                          <p>{invoiceData?.business_details?.country}</p>
                        </p>
                      </div>
                      <p>
                        VAT Number: {invoiceData?.business_details?.vat_number}
                      </p>
                    </div>
                  </div>

                  {/* Bill To Section */}
                  <div className="border-t border-b border-[#106A3A] py-6">
                    <h4 className="font-semibold">Bill To:</h4>
                    <p>{invoiceData?.property?.name}</p>
                    <p>{invoiceData?.property?.address_line1}</p>
                    <p>
                      {invoiceData?.property?.city},{" "}
                      {invoiceData?.property?.country}
                    </p>
                  </div>

                  {/* Invoice Table */}
                  <table className="w-full border border-[#FFF5CE]">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border p-2">Description</th>
                        <th className="border p-2">Quantity</th>
                        <th className="border p-2">Rate</th>
                        <th className="border p-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData?.tasks?.map((task, index) => (
                        <tr key={index}>
                          <td className="border p-2">{task.task_name}</td>
                          <td className="border p-2">{task.quantity}</td>
                          <td className="border p-2">£{task.rate}</td>
                          <td className="border p-2">£{task.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Total Amount Section */}
                  <div className="text-right border-t border-b border-[#106A3A] py-6">
                    <div className="flex flex-col gap-1 w-full justify-end items-end">
                      <div className="flex gap-2">
                        <p className="font-medium">Subtotal:</p>
                        <p>£{invoiceData?.subtotal}</p>
                      </div>
                      <div className="flex gap-2">
                        <p className="font-medium">VAT (%):</p>
                        <p>£{vatAmount ? vatAmount : "0.00"}</p>
                      </div>
                      <div className="flex gap-2 text-lg font-semibold">
                        <p>Total:</p>
                        {/* <p className="text-green-600">£{invoiceData?.subtotal}</p> */}
                        <p>£{formattedTotal}</p>
                      </div>
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div className="border-b border-[#106A3A] pb-6">
                    <h4 className="font-semibold">Payment Details:</h4>
                    <p>Account Name: {invoiceData?.bank_details?.account_name}</p>
                    <p>
                      Account Number: {invoiceData?.bank_details?.account_number}
                    </p>
                    <p>Sort Code: {invoiceData?.bank_details?.sort_code}</p>
                    <p className="text-gray-500">
                      Please make payment within{" "}
                      {invoiceData?.bank_details?.payment_terms} days.
                    </p>
                  </div>

                  {/* Cleaner Details */}
                  <div>
                    <p>Email: {invoiceData?.cleaner_email}</p>
                    <p>
                      Phone: {invoiceData?.cleaner_details?.phone_number || ""}
                    </p>
                  </div>
                </div>

                {/* Download Button */}
                <div className="flex w-full justify-end mt-6 mb-4">
                  <button
                    onClick={handleDownloadText}
                    className="flex items-center gap-3 bg-[#586989] px-4 py-3 rounded text-white font-medium"
                  >
                    <CiSaveDown1 className="text-[20px] font-semibold" />
                    Download Invoice
                  </button>
                </div>
              </>
            ) : (
              <p className="text-center text-gray-500  text-[20px] font-semibold py-4 h-[70vh] overflow-y-hidden">
                <div
                  ref={pdfRef}
                  className="border border-[#106A3A] p-6 rounded-[8px] flex flex-col gap-10 h-full w-full  items-center justify-center"
                >
                  The cleaner has not yet submitted their invoice.
                </div>
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Comment modal */}
      <CommentModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        invoiceId={invoiceData?.id}
        setInvoiceData={setInvoiceData}
      />
    </>
  );
}

export default InvoiceDetail;